import baseApi from "../../utils/baseApi";
import { User } from "../../types/userTypes";

const USERS_API = "/users";

class SuperAdminUserService {
    private static formatPhoneNumber(countryCode: string, phone: string): string {
        return `${countryCode} ${phone}`.trim();
    }

    static async getUsers(filters?: { 
        role?: string; 
        company_id?: string; 
        page?: number; 
        per_page?: number; 
    }): Promise<any> {
        try {
            const userData = JSON.parse(localStorage.getItem("user") || "{}");
            const userId = userData?.id;
    
            // Build query string from filters
            const queryParams = new URLSearchParams();
            if (filters?.role) queryParams.append("role", filters.role);
            if (filters?.company_id) queryParams.append("company_id", filters.company_id);
            if (filters?.page) queryParams.append("page", filters.page.toString());
            if (filters?.per_page) queryParams.append("per_page", filters.per_page.toString());
    
            const response = await baseApi.get(
                `${USERS_API}/userList/${userId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
            );
    
            return response.data; // this will now include pagination metadata too
        } catch (error: any) {
            console.error("Error fetching users:", error);
            throw new Error(error.response?.data?.message || "Failed to fetch users.");
        }
    }
    

    static async addUser(userData: Partial<User>): Promise<User> {
        try {
            const response = await baseApi.post(USERS_API, userData);
            return response.data.user;
        } catch (error: any) {
            console.error("Error adding user:", error);
            throw new Error(error.response?.data?.message || "Failed to add user.");
        }
    }

    static async removeUser(userId: number): Promise<void> {
        try {
            await baseApi.delete(`${USERS_API}/${userId}`);
        } catch (error: any) {
            console.error("Error deleting user:", error);
            throw new Error(error.response?.data?.message || "Failed to delete user.");
        }
    }

    static async getUserById(userId: number): Promise<User> {
        try {
            const response = await baseApi.get(`${USERS_API}/${userId}`);
            const user = response.data;
            return {
                ...user,
                phone: this.formatPhoneNumber(user.country_code, user.phone),
                created_at: new Date(user.created_at).toISOString().split("T")[0],
                updated_at: new Date(user.updated_at).toISOString().split("T")[0],
            };
        } catch (error: any) {
            console.error("Error fetching user details:", error);
            throw new Error(error.response?.data?.message || "Failed to fetch user details.");
        }
    }

    static async updateUser(userId: number, userData: Partial<User>): Promise<User> {
        try {
            const response = await baseApi.put(`${USERS_API}/${userId}`, userData);
            return response.data;
        } catch (error: any) {
            console.error("Error updating user:", error);
            throw new Error(error.response?.data?.message || "Failed to update user.");
        }
    }

    static async updateUserPassword(userId: number, newPassword: string): Promise<void> {
        try {
            await baseApi.put(`${USERS_API}/${userId}/updatePassword`, {
                new_password: newPassword,
                new_password_confirmation: newPassword,
            });
        } catch (error: any) {
            console.error("Error updating password:", error);
            throw new Error(error.response?.data?.message || "Failed to update user password.");
        }
    }

    static async assignAccess(userId: string, data: { folder_ids?: number[]; document_ids?: number[] }): Promise<void> {
        try {
            await baseApi.post(`${USERS_API}/assign-access/${userId}`, data);
        } catch (error: any) {
            console.error("Error assigning access:", error);
            throw new Error(error.response?.data?.message || "Failed to assign user access.");
        }
    }

    static async getAssignedAccess(userId: string, companyId: string): Promise<any> {
        try {
            const response = await baseApi.get(`${USERS_API}/assign-access/${userId}/${companyId}`);
            return response.data;
        } catch (error: any) {
            console.error("Error getting assigned access:", error);
            throw new Error(error.response?.data?.message || "Failed to get assigned user access.");
        }
    }
}

export default SuperAdminUserService;